/**
 * Error Boundary Component
 * Catches React component errors and displays a fallback UI
 */
import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Typography, Card, CardContent, Container } from '@mui/material';
import { ErrorOutline, Refresh, Home } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// Error display component (no hooks, can be used in class component)
const ErrorDisplay = ({ error, errorInfo }: { error: Error | null; errorInfo: ErrorInfo | null }) => {
  const { t } = useTranslation();
  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Card
        sx={{
          boxShadow: 4,
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            p: 4,
            textAlign: 'center',
          }}
        >
          <ErrorOutline sx={{ fontSize: 80, mb: 2 }} />
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {t('components.errorBoundary.title')}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            {t('components.errorBoundary.subtitle')}
          </Typography>
        </Box>

        <CardContent sx={{ p: 4 }}>
          {error && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" color="error" gutterBottom fontWeight={600}>
                {t('components.errorBoundary.errorDetails')}
              </Typography>
              <Box
                sx={{
                  bgcolor: 'error.light',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'error.main',
                }}
              >
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: 'error.dark',
                  }}
                >
                  {error.toString()}
                </Typography>
              </Box>
            </Box>
          )}

          {errorInfo && process.env.NODE_ENV === 'development' && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom fontWeight={600}>
                {t('components.errorBoundary.stackTrace')}
              </Typography>
              <Box
                sx={{
                  bgcolor: 'action.hover',
                  p: 2,
                  borderRadius: 1,
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {errorInfo.componentStack}
                </Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<Refresh />}
              onClick={handleReload}
              sx={{ minWidth: 150 }}
            >
              {t('components.errorBoundary.reload')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<Home />}
              onClick={handleGoHome}
              sx={{ minWidth: 150 }}
            >
              {t('components.errorBoundary.goHome')}
            </Button>
          </Box>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('components.errorBoundary.contactSupport')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

class ErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <ErrorDisplay error={this.state.error} errorInfo={this.state.errorInfo} />;
    }

    return this.props.children;
  }
}

// Export the class component
const ErrorBoundary = (props: Props) => <ErrorBoundaryClass {...props} />;

export default ErrorBoundary;

